/*
 * Sticky add-to-cart bar.
 *
 * Deliberately owns NO product form. It proxies the main product form's submit button
 * so Dawn's product-form.js remains the single source of truth for variant id, quantity,
 * cart drawer/notification and error handling. Price + disabled/sold-out state are
 * mirrored from the main product info, so they stay correct on variant change with zero
 * duplicated money-formatting or availability logic.
 */
if (!customElements.get('sticky-add-to-cart')) {
  customElements.define(
    'sticky-add-to-cart',
    class StickyAddToCart extends HTMLElement {
      connectedCallback() {
        this.button = this.querySelector('.sticky-atc__button');
        this.buttonLabel = this.querySelector('.sticky-atc__button-label');
        this.priceTarget = this.querySelector('.sticky-atc__price');

        // Rendered exactly once per product page by Dawn.
        this.mainForm = document.querySelector('product-form');
        this.mainSubmit = this.mainForm && this.mainForm.querySelector('.product-form__submit');
        this.mainPrice =
          document.querySelector('.product__info-container [id^="price-"]') ||
          document.querySelector('[id^="price-"]');

        // Nothing to proxy to (e.g. product unavailable / template without a buy form):
        // keep the bar out of the layout and the a11y tree entirely.
        if (!this.mainSubmit) {
          this.hidden = true;
          return;
        }

        // A blank author label means "follow the main button" (Add to cart / Sold out /
        // Unavailable, already localized). An explicit label stays fixed in all states.
        this.hasCustomLabel = this.hasAttribute('data-custom-label');
        this.customLabel = this.buttonLabel ? this.buttonLabel.textContent.trim() : '';

        this.proxyClick = this.proxyClick.bind(this);
        this.button.addEventListener('click', this.proxyClick);

        this.setupPriceMirror();
        this.setupStateMirror();
        this.setupVisibility();
      }

      disconnectedCallback() {
        if (this.button) this.button.removeEventListener('click', this.proxyClick);
        if (this.priceObserver) this.priceObserver.disconnect();
        if (this.stateObserver) this.stateObserver.disconnect();
        if (this.visibilityObserver) this.visibilityObserver.disconnect();
      }

      proxyClick(event) {
        event.preventDefault();
        if (this.mainSubmit.disabled || this.mainSubmit.hasAttribute('disabled')) return;
        this.mainSubmit.click();
      }

      // Mirror the main price node's markup so currency, sale, unit price and
      // per-variant updates all come through without re-implementing formatting.
      setupPriceMirror() {
        if (!this.priceTarget || !this.mainPrice) return;
        const sync = () => {
          this.priceTarget.innerHTML = this.mainPrice.innerHTML;
        };
        sync();
        this.priceObserver = new MutationObserver(sync);
        this.priceObserver.observe(this.mainPrice, {
          childList: true,
          subtree: true,
          characterData: true,
        });
      }

      // Mirror disabled + localized label (Sold out / Unavailable) from the main button.
      setupStateMirror() {
        const sync = () => {
          const disabled = this.mainSubmit.disabled || this.mainSubmit.hasAttribute('disabled');
          this.button.disabled = disabled;
          if (!this.buttonLabel) return;
          if (this.hasCustomLabel) {
            // Author set an explicit label — keep it even when disabled.
            this.buttonLabel.textContent = this.customLabel;
            return;
          }
          const mainSpan = this.mainSubmit.querySelector('span');
          if (mainSpan) this.buttonLabel.textContent = mainSpan.textContent.trim();
        };
        sync();
        this.stateObserver = new MutationObserver(sync);
        this.stateObserver.observe(this.mainSubmit, {
          attributes: true,
          attributeFilter: ['disabled'],
          childList: true,
          subtree: true,
          characterData: true,
        });
      }

      // Reveal the bar only while the main buy-buttons are off-screen.
      setupVisibility() {
        const sentinel = this.mainForm || this.mainSubmit;
        if (!('IntersectionObserver' in window) || !sentinel) {
          this.classList.add('sticky-atc--visible');
          return;
        }
        this.visibilityObserver = new IntersectionObserver(
          (entries) => {
            this.classList.toggle('sticky-atc--visible', !entries[0].isIntersecting);
          },
          { threshold: 0 }
        );
        this.visibilityObserver.observe(sentinel);
      }
    }
  );
}
